/**
 * Resolve a familiar Singapore area name from free-text address
 * (MRT / neighbourhood style — not opaque URA labels like "Downtown Core").
 *
 * Priority: landmark/street aliases → named area in text → postal prefixes.
 */

/** Friendly area labels shown in CRM (MRT / neighbourhood style). */
export const SINGAPORE_DISTRICTS = [
  'Choa Chu Kang',
  'Bukit Panjang',
  'Bukit Timah',
  'Bukit Batok',
  'Bukit Merah',
  'Marine Parade',
  'Tanjong Pagar',
  'Raffles Place',
  'Marina Bay',
  'HarbourFront',
  'Little India',
  'Clarke Quay',
  'River Valley',
  'Ang Mo Kio',
  'Jurong East',
  'Jurong West',
  'Pasir Ris',
  'Paya Lebar',
  'Queenstown',
  'Serangoon',
  'Sembawang',
  'Woodlands',
  'Toa Payoh',
  'Tampines',
  'Clementi',
  'Hougang',
  'Punggol',
  'Sengkang',
  'Boon Lay',
  'Changi Airport',
  'Lim Chu Kang',
  'Sungei Kadut',
  'Buona Vista',
  'Dhoby Ghaut',
  'City Hall',
  'Chinatown',
  'Lavender',
  'MacPherson',
  'Eunos',
  'Bedok',
  'Bishan',
  'Novena',
  'Orchard',
  'Outram',
  'Bugis',
  'Seletar',
  'Tanglin',
  'Tengah',
  'Newton',
  'Pioneer',
  'Mandai',
  'Changi',
  'Yishun',
  'Tuas',
  'Geylang',
  'Kallang',
  'Holland Village',
  'Telok Blangah',
  'Sentosa',
  'Redhill',
  'Commonwealth',
  'Dover',
  'Kent Ridge',
  'one-north',
  'Farrer Park',
  'Potong Pasir',
  'Kovan',
  'Serangoon Gardens',
  'Upper Thomson',
  'Marymount',
  'Braddell',
  'Bartley',
  'Tai Seng',
  'Ubi',
  'Kaki Bukit',
  'Tanah Merah',
  'Expo',
  'Loyang',
  'Simei',
  'Tampines East',
  'Pasir Ris East',
  'Punggol Point',
  'Sengkang West',
  'Marsiling',
  'Admiralty',
  'Canberra',
  'Khatib',
  'Yio Chu Kang',
  'Buangkok',
  'Bakau',
  'Joo Koon',
  'Gul Circle',
  'Tuas Link',
  'Lakeside',
  'Chinese Garden',
  'Beauty World',
  'King Albert Park',
  'Sixth Avenue',
  'Tan Kah Kee',
  'Botanic Gardens',
  'Stevens',
  'Napier',
  'Orchard Boulevard',
  'Somerset',
  'Great World',
  'Havelock',
  'Tiong Bahru',
  'Maxwell',
  'Shenton Way',
  'Cecil',
  'Anson',
] as const;

/**
 * Landmark / street / estate → familiar area name.
 * Longer phrases matched first.
 */
const PLACE_ALIASES: Array<[string, string]> = [
  // CBD / Tanjong Pagar / Marina
  ['gopeng street', 'Tanjong Pagar'],
  ['carlton city', 'Tanjong Pagar'],
  ['tanjong pagar', 'Tanjong Pagar'],
  ['tg pagar', 'Tanjong Pagar'],
  ['maxwell road', 'Tanjong Pagar'],
  ['maxwell', 'Tanjong Pagar'],
  ['anson road', 'Tanjong Pagar'],
  ['anson', 'Tanjong Pagar'],
  ['shenton way', 'Shenton Way'],
  ['cecil street', 'Raffles Place'],
  ['raffles place', 'Raffles Place'],
  ['raffles quay', 'Raffles Place'],
  ['marina bay sands', 'Marina Bay'],
  ['marina bay', 'Marina Bay'],
  ['marina square', 'Marina Bay'],
  ['collyer quay', 'Raffles Place'],
  ['battery road', 'Raffles Place'],
  ['robinson road', 'Raffles Place'],
  ['telok ayer', 'Chinatown'],
  ['chinatown', 'Chinatown'],
  ['club street', 'Chinatown'],
  ['amoy street', 'Chinatown'],
  ['boat quay', 'Clarke Quay'],
  ['clarke quay', 'Clarke Quay'],
  ['riverside point', 'Clarke Quay'],
  ['city hall', 'City Hall'],
  ['capitol', 'City Hall'],
  ['bugis', 'Bugis'],
  ['beach road', 'Bugis'],
  ['north bridge road', 'Bugis'],
  ['little india', 'Little India'],
  ['serangoon road', 'Little India'],
  ['dhoby ghaut', 'Dhoby Ghaut'],
  ['somerset', 'Orchard'],
  ['orchard road', 'Orchard'],
  ['orchard', 'Orchard'],
  ['tanglin', 'Tanglin'],
  ['river valley', 'River Valley'],
  ['great world', 'Great World'],
  ['havelock', 'Havelock'],
  ['tiong bahru', 'Tiong Bahru'],
  ['outram', 'Outram'],
  ['harbourfront', 'HarbourFront'],
  ['harbour front', 'HarbourFront'],
  ['vivocity', 'HarbourFront'],
  ['telok blangah', 'Telok Blangah'],
  ['mount faber', 'Sentosa'],
  ['sentosa cove', 'Sentosa'],
  ['sentosa', 'Sentosa'],
  ['siloso', 'Sentosa'],
  ['keppel', 'HarbourFront'],

  // North — Admiralty / Sembawang
  ['foodxchange @ admiralty', 'Sembawang'],
  ['food xchange @ admiralty', 'Sembawang'],
  ['foodxchange admiralty', 'Sembawang'],
  ['food xchange admiralty', 'Sembawang'],
  ['admiralty street', 'Sembawang'],
  ['admiralty road west', 'Sembawang'],
  ['admiralty link', 'Admiralty'],
  ['admiralty', 'Admiralty'],
  ['senoko', 'Sembawang'],
  ['sembawang', 'Sembawang'],
  ['canberra', 'Canberra'],
  ['jalan malu malu', 'Canberra'],
  ['jalan malu-malu', 'Canberra'],
  ['jln malu malu', 'Canberra'],
  ['jln malu-malu', 'Canberra'],
  ['malu malu', 'Canberra'],
  ['malu-malu', 'Canberra'],
  ['kindol gardens', 'Canberra'],
  ['simpang', 'Canberra'],
  ['marsiling', 'Marsiling'],
  ['woodlands industrial', 'Woodlands'],
  ['woodlands spectrum', 'Woodlands'],
  ['woodlands avenue', 'Woodlands'],
  ['woodlands drive', 'Woodlands'],
  ['woodlands', 'Woodlands'],
  ['yishun', 'Yishun'],
  ['khatib', 'Khatib'],
  ['northpoint', 'Yishun'],

  // West
  ['sungei kadut', 'Sungei Kadut'],
  ['tuas link', 'Tuas Link'],
  ['tuas south', 'Tuas'],
  ['tuas bay', 'Tuas'],
  ['tuas west', 'Tuas'],
  ['tuas', 'Tuas'],
  ['gul circle', 'Gul Circle'],
  ['joo koon', 'Joo Koon'],
  ['pioneer', 'Pioneer'],
  ['boon lay', 'Boon Lay'],
  ['lakeside', 'Lakeside'],
  ['chinese garden', 'Chinese Garden'],
  ['jurong east', 'Jurong East'],
  ['jurong west', 'Jurong West'],
  ['international business park', 'Jurong East'],
  ['tukang', 'Jurong East'],
  ['pandan', 'Jurong East'],
  ['chin bee', 'Jurong East'],
  ['fishery port', 'Jurong West'],
  ['clementi', 'Clementi'],
  ['west coast', 'Clementi'],
  ['dover', 'Dover'],
  ['buona vista', 'Buona Vista'],
  ['one-north', 'one-north'],
  ['one north', 'one-north'],
  ['kent ridge', 'Kent Ridge'],
  ['holland village', 'Holland Village'],
  ['sixth avenue', 'Sixth Avenue'],
  ['botanic gardens', 'Botanic Gardens'],
  ['bukit timah', 'Bukit Timah'],
  ['bukit batok', 'Bukit Batok'],
  ['bukit panjang', 'Bukit Panjang'],
  ['choa chu kang', 'Choa Chu Kang'],
  ['tengah', 'Tengah'],
  ['lim chu kang', 'Lim Chu Kang'],
  ['mandai', 'Mandai'],

  // East / North-East
  ['changi airport', 'Changi Airport'],
  ['airport boulevard', 'Changi Airport'],
  ['changi business park', 'Changi'],
  ['changi', 'Changi'],
  ['expo', 'Expo'],
  ['tanah merah', 'Tanah Merah'],
  ['bedok', 'Bedok'],
  ['tampines', 'Tampines'],
  ['simei', 'Simei'],
  ['pasir ris', 'Pasir Ris'],
  ['loyang', 'Loyang'],
  ['paya lebar', 'Paya Lebar'],
  ['eunos', 'Eunos'],
  ['ubi', 'Ubi'],
  ['tai seng', 'Tai Seng'],
  ['macpherson', 'MacPherson'],
  ['defu', 'Hougang'],
  ['hougang', 'Hougang'],
  ['kovan', 'Kovan'],
  ['serangoon', 'Serangoon'],
  ['punggol', 'Punggol'],
  ['sengkang', 'Sengkang'],
  ['buangkok', 'Buangkok'],
  ['seletar', 'Seletar'],
  ['yio chu kang', 'Yio Chu Kang'],
  ['ang mo kio', 'Ang Mo Kio'],
  ['bishan', 'Bishan'],
  ['marymount', 'Marymount'],
  ['braddell', 'Braddell'],
  ['toa payoh', 'Toa Payoh'],
  ['potong pasir', 'Potong Pasir'],
  ['lavender', 'Lavender'],
  ['kallang', 'Kallang'],
  ['geylang', 'Geylang'],
  ['marine parade', 'Marine Parade'],
  ['katong', 'Marine Parade'],
  ['novena', 'Novena'],
  ['newton', 'Newton'],
  ['farrer park', 'Farrer Park'],
  ['redhill', 'Redhill'],
  ['queenstown', 'Queenstown'],
  ['commonwealth', 'Commonwealth'],
  ['alexandra', 'Redhill'],
  ['bukit merah', 'Bukit Merah'],

  // Short aliases
  ['amk', 'Ang Mo Kio'],
  ['cck', 'Choa Chu Kang'],
];

/**
 * Postal prefix → familiar area (4 → 3 → 2 digit lookup).
 * Avoids opaque labels like Downtown Core.
 */
const POSTAL_PREFIX_TO_DISTRICT: Record<string, string> = {
  // Tanjong Pagar / Maxwell / Gopeng (078862 etc.)
  '0788': 'Tanjong Pagar',
  '0789': 'Tanjong Pagar',
  '0790': 'Tanjong Pagar',
  '0791': 'Tanjong Pagar',
  '0792': 'Tanjong Pagar',
  '0793': 'Tanjong Pagar',
  '0688': 'Tanjong Pagar',
  '0689': 'Tanjong Pagar',
  '0690': 'Tanjong Pagar',
  '0691': 'Tanjong Pagar',
  '0692': 'Tanjong Pagar',
  '078': 'Tanjong Pagar',
  '079': 'Tanjong Pagar',
  '068': 'Tanjong Pagar',
  '069': 'Tanjong Pagar',

  // Raffles / Marina
  '018': 'Marina Bay',
  '019': 'Marina Bay',
  '048': 'Raffles Place',
  '049': 'Raffles Place',

  // Admiralty industrial → Sembawang
  '7574': 'Sembawang',
  '7575': 'Sembawang',
  '7576': 'Sembawang',
  '7577': 'Sembawang',
  '7578': 'Sembawang',
  '7579': 'Sembawang',
  '757': 'Sembawang',
  '758': 'Sembawang',
  '759': 'Sembawang',

  // Canberra / Simpang (Jalan Malu Malu etc.) — not Yishun
  '7696': 'Canberra',
  '7697': 'Canberra',
  '7698': 'Canberra',
  '7699': 'Canberra',
  '769': 'Canberra',

  // Sentosa / Mount Faber (sector 09) — more specific than generic Telok Blangah
  '0980': 'Sentosa',
  '0981': 'Sentosa',
  '0982': 'Sentosa',
  '0983': 'Sentosa',
  '0984': 'Sentosa',
  '0985': 'Sentosa',
  '0986': 'Sentosa',
  '0987': 'Sentosa',
  '0988': 'Sentosa',
  '0989': 'Sentosa',
  '098': 'Sentosa',
  '0995': 'Sentosa',
  '0996': 'Sentosa',
  '0997': 'Sentosa',
  '0998': 'Sentosa',
  '0999': 'Sentosa',
  // Mount Faber Peak / cable car (0992xx) — same leisure cluster as Sentosa for CRM outlets
  '0992': 'Sentosa',
  '0993': 'Sentosa',
  '0994': 'Sentosa',
  '099': 'Sentosa',
  // 2-digit sectors → familiar names (not URA "Downtown Core")
  '01': 'Raffles Place',
  '02': 'Chinatown',
  '03': 'Chinatown',
  '04': 'Raffles Place',
  '05': 'Raffles Place',
  '06': 'Raffles Place',
  '07': 'Tanjong Pagar',
  '08': 'Tanjong Pagar',
  '09': 'Telok Blangah',
  '10': 'HarbourFront',
  '11': 'Pasir Panjang',
  '12': 'Clementi',
  '13': 'Clementi',
  '14': 'Queenstown',
  '15': 'Queenstown',
  '16': 'Tiong Bahru',
  '17': 'City Hall',
  '18': 'Bugis',
  '19': 'Lavender',
  '20': 'Little India',
  '21': 'Little India',
  '22': 'Chinatown',
  '23': 'Outram',
  '24': 'River Valley',
  '25': 'Orchard',
  '26': 'Orchard',
  '27': 'Tanglin',
  '28': 'Novena',
  '29': 'Novena',
  '30': 'Novena',
  '31': 'Toa Payoh',
  '32': 'Toa Payoh',
  '33': 'Toa Payoh',
  '34': 'MacPherson',
  '35': 'MacPherson',
  '36': 'Geylang',
  '37': 'Geylang',
  '38': 'Geylang',
  '39': 'Geylang',
  '40': 'Eunos',
  '41': 'Paya Lebar',
  '42': 'Marine Parade',
  '43': 'Marine Parade',
  '44': 'Marine Parade',
  '45': 'Marine Parade',
  '46': 'Bedok',
  '47': 'Bedok',
  '48': 'Bedok',
  '49': 'Changi',
  '50': 'Pasir Ris',
  '51': 'Pasir Ris',
  '52': 'Tampines',
  '53': 'Hougang',
  '54': 'Hougang',
  '55': 'Serangoon',
  '56': 'Ang Mo Kio',
  '57': 'Bishan',
  '58': 'Bukit Timah',
  '59': 'Bukit Timah',
  '60': 'Jurong East',
  '61': 'Jurong West',
  '62': 'Jurong West',
  '63': 'Pioneer',
  '64': 'Jurong West',
  '65': 'Bukit Batok',
  '66': 'Bukit Batok',
  '67': 'Bukit Panjang',
  '68': 'Choa Chu Kang',
  '69': 'Tengah',
  '70': 'Tengah',
  '71': 'Lim Chu Kang',
  '72': 'Woodlands',
  '73': 'Woodlands',
  '75': 'Yishun',
  '76': 'Yishun',
  '77': 'Seletar',
  '78': 'Seletar',
  '79': 'Seletar',
  '80': 'Punggol',
  '81': 'Punggol',
  '82': 'Sengkang',
};

/** Old URA-style labels → friendlier names (for any leftover stored values). */
const LEGACY_DISTRICT_LABELS: Record<string, string> = {
  'downtown core': 'Raffles Place',
  'singapore river': 'Clarke Quay',
  'straits view': 'Marina Bay',
  'marina east': 'Marina Bay',
  'marina south': 'Marina Bay',
  museum: 'Dhoby Ghaut',
  rochor: 'Bugis',
  outram: 'Outram',
  'western islands': 'Tuas',
  'southern islands': 'Sentosa',
  'changi bay': 'Changi',
  'central water catchment': 'Upper Thomson',
  'western water catchment': 'Lim Chu Kang',
  'north-eastern islands': 'Punggol',
};

/** 6-digit Singapore postal code from free text (prefers last match). */
export function extractSingaporePostal(address?: string | null): string | null {
  const text = (address ?? '').trim();
  if (!text) return null;
  const matches = text.match(/\b([0-9]{6})\b/g);
  if (!matches?.length) return null;
  return matches[matches.length - 1] ?? null;
}

function matchPlaceAlias(addressLower: string): string | null {
  // Treat hyphen/en-dash like spaces so "Jalan Malu-Malu" matches "jalan malu malu".
  const normalized = addressLower.replace(/[\u2010-\u2015\u2212\-]+/g, ' ').replace(/\s+/g, ' ');
  const sorted = [...PLACE_ALIASES].sort((a, b) => b[0].length - a[0].length);
  for (const [phrase, district] of sorted) {
    if (normalized.includes(phrase) || addressLower.includes(phrase)) return district;
  }
  return null;
}

function matchDistrictName(addressLower: string): string | null {
  const sorted = [...SINGAPORE_DISTRICTS].sort((a, b) => b.length - a.length);
  for (const name of sorted) {
    if (addressLower.includes(name.toLowerCase())) return name;
  }
  return null;
}

function districtFromPostal(postal: string): string | null {
  const digits = postal.replace(/\D/g, '');
  if (digits.length < 2) return null;
  for (const len of [4, 3, 2] as const) {
    if (digits.length < len) continue;
    const prefix = digits.slice(0, len);
    const hit = POSTAL_PREFIX_TO_DISTRICT[prefix];
    if (hit) return hit;
  }
  return null;
}

function friendlyLegacyLabel(label: string): string {
  const mapped = LEGACY_DISTRICT_LABELS[label.trim().toLowerCase()];
  return mapped ?? label.trim();
}

/** Area name from a 6-digit (or prefix) Singapore postal code. */
export function resolveDistrictFromPostal(postal?: string | null): string {
  const digits = (postal ?? '').replace(/\D/g, '');
  if (!digits) return '';
  return districtFromPostal(digits) ?? '';
}

/** Auto area name from address; empty when unknown. */
export function resolveDistrictFromAddress(address?: string | null): string {
  const text = (address ?? '').trim();
  if (!text) return '';

  const lower = text.toLowerCase();

  const fromPlace = matchPlaceAlias(lower);
  if (fromPlace) return fromPlace;

  const named = matchDistrictName(lower);
  if (named) return named;

  const postal = extractSingaporePostal(text);
  if (postal) {
    const fromPostal = districtFromPostal(postal);
    if (fromPostal) return fromPostal;
  }

  return '';
}

/** UI district: prefer office address; else ACRA registered address when office is empty. */
export type ClientDistrictSource = 'office' | 'acra' | '';

export function resolveClientDistrict(client: {
  district?: string | null;
  address?: string | null;
  intel_profile_json?: { registeredAddress?: string | null } | null;
}): { district: string; source: ClientDistrictSource } {
  const office = client.address?.trim() ?? '';
  if (office) {
    const fromAddress = resolveDistrictFromAddress(office);
    if (fromAddress) return { district: fromAddress, source: 'office' };
    const stored = (client.district ?? '').trim();
    if (stored) return { district: friendlyLegacyLabel(stored), source: 'office' };
    return { district: '', source: '' };
  }

  const registered = client.intel_profile_json?.registeredAddress?.trim() ?? '';
  if (registered) {
    const fromAcra = resolveDistrictFromAddress(registered);
    if (fromAcra) return { district: fromAcra, source: 'acra' };
  }

  return { district: '', source: '' };
}

export function displayClientDistrict(client: {
  district?: string | null;
  address?: string | null;
  intel_profile_json?: { registeredAddress?: string | null } | null;
}): string {
  return resolveClientDistrict(client).district;
}
